import React, { useState, useEffect } from 'react'
import {ReactComponent as CloseIcon} from '../../assets/Close-btn.svg'
import { InputAdornment, OutlinedInput, Snackbar, Alert } from '@mui/material';
import {ReactComponent as EyeIcon} from '../../assets/Eye.svg'
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { browserLocalPersistence, browserSessionPersistence, createUserWithEmailAndPassword, setPersistence, signInWithEmailAndPassword, updateProfile, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import CustomDropdown from './CustomDropdown';
import { useNavigate } from 'react-router-dom';
import { UserService } from '../../services/UserService';
import { getOrCreateBrowserId } from '../../utils/browserId';
import i18n from '../../locales/i18n';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../AppContext';
import styles from './Inputs.module.css';

interface LoginProps{
    onClose:()=>void
    setType:(value:string)=>void
}

const Register:React.FC<LoginProps>=({onClose,setType})=> {
    const navigate = useNavigate();
    const { setUser, setShowModal: setAuthModal } = useAppContext();
    const [isRTL, setIsRTL] = useState<boolean>(i18n.language === 'ar');
    const { t } = useTranslation();
    const [email,setEmail]=useState<string>('');
    const [fullname,setFullName]=useState<string>('');
    const [password,setPassword]=useState<string>('');
    const [confirmPassword,setConfirmPassword]=useState<string>('');
    const [role,setRole]=useState<string>('Choose your role');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [rememberMeChecked, setRememberMeChecked] = useState<boolean>(false);
    const [errorMessage,setErrorMessage]=useState<string>('')
    const [loading,setLoading]=useState<boolean>(false)
    const [termsRead,setTermsRead]=useState<boolean>(false)
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    // const [showModal, setShowModal] = useState<boolean>(false);
    useEffect(() => {
        const handleLanguageChange = () => {
            setIsRTL(i18n.language === 'ar');
        };
        
        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        const handleClickOutside = (event: MouseEvent) => {
            const modal = document.getElementById('register-modal');
            if (modal && !modal.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const handleRememberMeChange = () => {
        setRememberMeChecked(!rememberMeChecked);
    };
    
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };
    
    const handleRegister = async () => {
        if (!email || !password || !fullname || !termsRead || !confirmPassword) {
            setErrorMessage(t('auth.validationError'));
            return;
        }

        if (role === 'Choose your role') {
            setErrorMessage(t('auth.pleaseSelectRole'));
            return;
        }

        if (password !== confirmPassword) {
            setErrorMessage(t('auth.passwordsDontMatch'));
            return;
        }

        if (password.length < 8) {
            setErrorMessage(t('auth.passwordTooShort'));
            return;
        }

        setLoading(true);
        setErrorMessage(''); 
        try {
            const browserId = getOrCreateBrowserId();
            const persistence = rememberMeChecked ? browserLocalPersistence : browserSessionPersistence;
            await setPersistence(auth, persistence);
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            
            await UserService.createUser(fullname, role);
            await updateProfile(userCredential.user, {
                displayName: fullname
            });
            await UserService.setUUID(browserId).catch((error) => { 
                console.error(error);
            });
            
            // Wait for auth state to be updated
            await new Promise((resolve) => {
                const unsubscribe = onAuthStateChanged(auth, async (user) => {
                    if (user) {
                        try {
                            const userData = await UserService.getUser();
                            setUser(userData);
                            unsubscribe();
                            resolve(true);
                        } catch (error) {
                            console.error('Error fetching user data:', error);
                            unsubscribe();
                            resolve(false);
                        }
                    }
                });
            });

            setSuccessMessage(t('auth.registrationSuccess'));
            setShowSuccess(true);
            setAuthModal(false);
            onClose();
            navigate('/');
        } catch (error: any) {
            if (error.code === 'auth/email-already-in-use') {
                setErrorMessage(t('auth.emailInUse'));
            } else if (error.code === 'auth/invalid-email') {
                setErrorMessage(t('auth.invalidEmail'));
            } else {
                setErrorMessage(t('auth.registrationError'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSuccess = () => {
        setShowSuccess(false);
    };

    return (
        <div className={`w-full ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className='flex flex-col md:flex-row gap-5 md:gap-[32px]'>
                <div className='flex flex-col flex-1'> 
                    <label htmlFor="FullName" className='font-normal md:text-[18px] mb-[7px] leading-none'>{t('auth.fullName')}</label>
                    <input placeholder={t('auth.fullNamePlaceholder')} className={`stylesinput px-[12px] md:px-[20px] pt-[8px] md:pt-[10px] pb-[8px] md:pb-[10px] border-[1px] rounded-[5px] border-[rgba(102,102,102,0.35)] ${isRTL ? 'text-right' : 'text-left'}}`} type="text" value={fullname} onChange={(e)=>{setFullName(e.target.value); setErrorMessage('')}} required/>
                </div>

                <div className='flex flex-col flex-1'> 
                    <label htmlFor="Email" className='font-normal md:text-[18px] mb-[7px] leading-none'>{t('auth.email')}</label>
                    <input placeholder={t('auth.emailPlaceholder')} className={`input px-[12px] md:px-[20px] pt-[8px] md:pt-[10px] pb-[8px] md:pb-[10px] border-[1px] rounded-[5px] border-[rgba(102,102,102,0.35)] ${isRTL ? 'text-right' : 'text-left'}}`} type="email" value={email} onChange={(e)=>{setEmail(e.target.value); setErrorMessage('')}} required />
                </div>
            </div>
            <div className='flex flex-col md:flex-row gap-5 md:gap-[32px] mt-5 md:mt-0'>
                <div className='flex flex-1 flex-col md:mt-[25px]'>
                    <label htmlFor="Password" className='font-normal md:text-[18px] mb-[7px] leading-none'>{t('auth.password')}</label>
                    <OutlinedInput
                        value={password}
                        onChange={(e)=>setPassword(e.target.value)}
                        id="outlined-adornment-password"
                        placeholder={t('auth.passwordPlaceholder')}
                        className={`border-[rgba(102,102,102,0.35)] ${isRTL ? 'text-right' : 'text-left'} ${styles['leading-normal']}`} 
                        type={showPassword ? 'text' : 'password'}
                        endAdornment={
                            <InputAdornment position="end">
                                <EyeIcon
                                    className='cursor-pointer'  
                                    aria-label={showPassword ? 'hide the password' : 'display the password'}
                                    onClick={handleClickShowPassword}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </EyeIcon>
                            </InputAdornment>
                        }
                    />
                </div>

                <div className='flex flex-1 flex-col md:mt-[25px]'>
                    <label htmlFor="ConfirmPassword" className='font-normal md:text-[18px] mb-[7px] leading-none'>{t('auth.confirmPassword')}</label>
                    <OutlinedInput
                        value={confirmPassword}
                        onChange={(e)=>setConfirmPassword(e.target.value)}
                        id="outlined-adornment-confirm-password"
                        placeholder={t('auth.confirmPasswordPlaceholder')}
                        className={`border-[rgba(102,102,102,0.35)] ${isRTL ? 'text-right' : 'text-left'} ${styles['leading-normal']}`} 
                        type={showConfirmPassword ? 'text' : 'password'}
                        endAdornment={
                            <InputAdornment position="end">
                                <EyeIcon
                                    className='cursor-pointer'  
                                    aria-label={showConfirmPassword ? 'hide the password' : 'display the password'}
                                    onClick={handleClickShowConfirmPassword}
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </EyeIcon>
                            </InputAdornment>
                        }
                    />
                </div>
            </div>

            <div className='mt-5 md:mt-[25px]'>
                <label htmlFor="Role" className='block font-normal md:text-[18px] mb-[7px] leading-none'>{t('auth.role')}</label>
                <CustomDropdown selectedOption={role} options={['Student', 'Teacher','Other']} onChange={(value:string)=>setRole(value)} />          
            </div>

            <button className='flex mt-[25px] items-center gap-3' onClick={()=>setTermsRead(!termsRead)}>
                <input type="checkbox" checked={termsRead} className="filter-checkbox" onChange={(e:any)=>setTermsRead(!termsRead)} required/>
                <p className='font-semibold'>
                    <label className='text-[14px] md:text-[16px] text-black md:text-[#636363] font-semibold cursor-pointer' htmlFor={`checkbox-${1}`}>
                        {t('auth.termsAgreement')}{' '}
                        <a href="/terms-and-conditions" className='text-[#00726B] underline hover:text-[#005a54]' target="_blank" rel="noopener noreferrer">{t('auth.termsOfService')}</a>
                        {' '}&{' '}
                        <a href="/privacy-policies" className='text-[#00726B] underline hover:text-[#005a54]' target="_blank" rel="noopener noreferrer">{t('auth.privacyPolicy')}</a>
                        {' '}{t('auth.ofMMT')}
                    </label>
                </p>
            </button>

            <button
                className='bg-[#00726B] text-[18px] font-semibold w-full text-white rounded-[12px] md:rounded-[5px] mt-[30px] py-[18px] md:py-[10px] hover:bg-[#005a54] transition-colors duration-200'
                onClick={handleRegister}
                disabled={loading}
            >
                {loading ? t('auth.registering') : t('auth.signUp')}
            </button>

            {errorMessage && (
                <p className='text-red text-center mt-2'>{errorMessage}</p>
            )}

            <p className='text-[18px] md:text-[20px] w-full text-center mt-[20px] text-[#4D4D4D]'>
                {t('auth.alreadyHaveAccount')}{' '}
                <button className='underline text-[#00726B] hover:text-[#005a54] py-1' onClick={()=>setType('login')}>
                    {t('auth.signIn')}
                </button>
            </p>

            <Snackbar open={showSuccess} autoHideDuration={2000} onClose={handleCloseSuccess}>
                <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Register;